import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, RefreshCw, Trash2 } from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, ErrorState } from '@/acc-ui/primitives/Layout';
import { DataTable, type Column } from '@/acc-ui/primitives/DataTable';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { Input } from '@/acc-ui/primitives/Input';
import { SearchInput } from '@/acc-ui/primitives/SearchInput';
import { HighlightText } from '@/acc-ui/primitives/HighlightText';
import { Dialog } from '@/acc-ui/primitives/Dialog';
import { toast } from '@/acc-ui/primitives/Toast';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { ingestionApi, type LoanFileListItem } from '@/api/ingestion-api';
import { usePermission } from '@/auth/use-permission';
import { INGESTION_UPLOAD, LOAN_FILE_STATE } from '@loanaudit/shared';

/**
 * /loan-files — the workspace landing for ingested files.
 * Filters: state, product, branch, search, date range.
 */

export function LoanFilesPage() {
  const navigate = useNavigate();
  const { has } = usePermission();
  const canUpload = has(INGESTION_UPLOAD);

  // View state (filters + sort + page) is initialised from and mirrored to
  // the URL so refresh / deep-link / back preserve the view.
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = 25;
  const [page, setPage] = useState(() => Number(searchParams.get('page')) || 1);
  const [sortBy, setSortBy] = useState<string>(() => searchParams.get('sortBy') || 'receivedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    () => (searchParams.get('sortDir') === 'asc' ? 'asc' : 'desc'),
  );
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [state, setState] = useState<string>(() => searchParams.get('state') || '');
  const [from, setFrom] = useState<string>(() => searchParams.get('from') || '');
  const [to, setTo] = useState<string>(() => searchParams.get('to') || '');

  // Debounce the server-backed search so typing doesn't fire a request on
  // every keystroke — the query reacts to the settled value while the input
  // stays responsive to the live one.
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  // Mirror the view state to the URL (only non-defaults, so the bare URL
  // stays clean). replace: true keeps typing/filtering out of history.
  useEffect(() => {
    const p = new URLSearchParams();
    if (debouncedSearch) p.set('q', debouncedSearch);
    if (state) p.set('state', state);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (sortBy !== 'receivedAt') p.set('sortBy', sortBy);
    if (sortDir !== 'desc') p.set('sortDir', sortDir);
    if (page !== 1) p.set('page', String(page));
    setSearchParams(p, { replace: true });
  }, [debouncedSearch, state, from, to, sortBy, sortDir, page, setSearchParams]);

  const query = useQuery({
    queryKey: ['loanFiles', { page, pageSize, search: debouncedSearch, state, from, to, sortBy, sortDir }],
    queryFn: () => {
      // sortBy/sortDir aren't on LoanFilesQuery's type yet; the list() call
      // serialises the whole object to query params, so pass via a variable
      // to avoid the excess-property check.
      const listParams = {
        page,
        pageSize,
        search: debouncedSearch || undefined,
        state: state || undefined,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
        sortBy,
        sortDir,
      };
      return ingestionApi.list(listParams);
    },
    placeholderData: (prev) => prev,
  });

  // Soft-delete mutation. The backend marks deleted_at=now() on the row;
  // documents and findings stay in DB for compliance. On success we
  // invalidate the list cache so the row disappears immediately. Confirm
  // happens in a Dialog (see pendingDelete). The row click handler must not
  // trigger this (handled via stopPropagation on the button itself).
  const queryClient = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<LoanFileListItem | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (id: string) => ingestionApi.remove(id),
    onSuccess: () => {
      // Refetch every page of loanFiles, regardless of filter combination,
      // so the deleted row vanishes even if the user is on a non-matching
      // page or scrolling through pagination.
      queryClient.invalidateQueries({ queryKey: ['loanFiles'] });
      setPendingDelete(null);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Could not delete loan file: ${msg}`);
    },
  });

  const handleDeleteClick = (
    e: MouseEvent<HTMLButtonElement>,
    row: LoanFileListItem,
  ) => {
    // Stop the click from bubbling to the row, which would navigate to
    // the loan file detail page mid-delete.
    e.stopPropagation();
    setPendingDelete(row);
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const columns: Column<LoanFileListItem>[] = useMemo(
    () => [
      {
        key: 'loanNumber',
        header: 'Loan',
        sortable: true,
        cell: (r) => (
          <div className="flex flex-col">
            <span className="font-semibold text-text-primary">
              <HighlightText text={r.loanNumber} query={debouncedSearch} />
            </span>
            <span className="text-caption text-text-tertiary truncate">
              <HighlightText text={r.borrowerName} query={debouncedSearch} />
            </span>
          </div>
        ),
      },
      {
        key: 'product',
        header: 'Product',
        sortable: true,
        cell: (r) => (
          <div className="flex flex-col">
            <code className="font-mono text-caption text-text-secondary">{r.productCode}</code>
            <span className="text-micro text-text-muted">{r.branchCode}</span>
          </div>
        ),
        width: '12rem',
      },
      {
        key: 'state',
        header: 'State',
        sortable: true,
        cell: (r) => <StateBadge state={r.state} rejectReason={r.rejectReason ?? null} />,
        width: '11rem',
      },
      {
        key: 'documents',
        header: 'Docs',
        cell: (r) => (
          <span className="text-caption text-text-secondary tabular-nums">
            {r.documentCount}
          </span>
        ),
        align: 'right',
        width: '5rem',
      },
      {
        key: 'receivedAt',
        header: 'Received',
        sortable: true,
        cell: (r) => (
          <span className="text-caption text-text-tertiary tabular-nums">
            {new Date(r.receivedAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        ),
        width: '11rem',
      },
      {
        key: 'actions',
        header: '',
        cell: (r) => (
          <button
            type="button"
            onClick={(e) => handleDeleteClick(e, r)}
            disabled={deleteMutation.isPending && deleteMutation.variables === r.id}
            title={`Delete ${r.loanNumber}`}
            aria-label={`Delete loan file ${r.loanNumber}`}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-text-tertiary hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        ),
        align: 'right',
        width: '3rem',
      },
    ],
    // handleDeleteClick depends on deleteMutation; columns must re-create
    // when the mutation state changes so the disabled state stays in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteMutation.isPending, deleteMutation.variables, debouncedSearch],
  );

  return (
    <AppShell crumb="Workspace" title="Loan Files">
      <PageHeader
        title="Loan Files"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
              onClick={() => query.refetch()}
            >
              Refresh
            </Button>
            {canUpload && (
              <Button
                size="sm"
                leftIcon={<Upload className="w-4 h-4" />}
                onClick={() => navigate('/loan-files/new')}
              >
                Upload loan file
              </Button>
            )}
          </div>
        }
      />

      <Card padding={false}>
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold text-text-secondary">Loan number</span>
            <SearchInput
              placeholder="Search by loan number"
              value={search}
              onChange={(v) => { setSearch(v); setPage(1); }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold text-text-secondary">State</span>
            <select
              value={state}
              onChange={(e) => {
                setState(e.target.value);
                setPage(1);
              }}
              className="h-10 px-3 bg-surface-container-lowest border border-outline-variant rounded-lg text-label text-on-surface outline-none focus:border-primary/50"
            >
              <option value="">All states</option>
              {Object.values(LOAN_FILE_STATE).map((s) => (
                <option key={s} value={s}>
                  {STATE_META[s]?.label ?? s}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold text-text-secondary">From</span>
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold text-text-secondary">To</span>
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
          </label>
        </div>
      </Card>

      {query.isError ? (
        <ErrorState
          description="Could not load loan files."
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable<LoanFileListItem>
          columns={columns}
          rows={query.data?.items ?? []}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          rowKey={(r) => r.id}
          page={page}
          pageSize={pageSize}
          total={query.data?.total ?? 0}
          onPageChange={setPage}
          loading={query.isLoading}
          onRowClick={(r) => navigate(`/loan-files/${r.id}`)}
          emptyMessage={
            search || state || from || to ? (
              <div className="flex flex-col items-center gap-2">
                <span>
                  No loan files match
                  {debouncedSearch ? <> “<span className="font-medium text-text-secondary">{debouncedSearch}</span>”</> : ' the current filters'}.
                </span>
                <span className="text-caption text-text-tertiary">
                  Try fewer words, check the spelling, or search by loan number.
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => { setSearch(''); setState(''); setFrom(''); setTo(''); setPage(1); }}
                >
                  Clear filters
                </Button>
              </div>
            ) : (
              'No loan files yet. Upload one or run a connector sweep to get started.'
            )
          }
        />
      )}

      {pendingDelete && (
        <Dialog
          open
          onOpenChange={(v) => !v && setPendingDelete(null)}
          title={`Delete loan ${pendingDelete.loanNumber}?`}
          description="This hides the loan from the dashboard. Audit records, documents, and validation findings are retained for compliance and can be restored if needed."
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setPendingDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteMutation.mutate(pendingDelete.id)}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </>
          }
        >
          <p className="text-caption text-text-secondary">
            {pendingDelete.borrowerName}
          </p>
        </Dialog>
      )}
    </AppShell>
  );
}


// State → { tone, friendly label }. Shared by StateBadge and the State
// filter dropdown so both show the same friendly labels.
export const STATE_META: Record<
  string,
  { tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'muted'; label: string }
> = {
  draft: { tone: 'muted', label: 'Draft' },
  received: { tone: 'info', label: 'Received' },
  virus_scan_pending: { tone: 'warning', label: 'Scanning…' },
  virus_scanned: { tone: 'info', label: 'Scanned' },
  accepted: { tone: 'success', label: 'Accepted' },
  parsing_in_progress: { tone: 'warning', label: 'Parsing…' },
  parsed: { tone: 'success', label: 'Parsed' },
  parsing_failed: { tone: 'danger', label: 'Parse failed' },
  validation_in_progress: { tone: 'warning', label: 'Validating…' },
  validated: { tone: 'success', label: 'Validated' },
  validation_failed: { tone: 'danger', label: 'Validation failed' },
  rejected: { tone: 'danger', label: 'Rejected' },
};

export function StateBadge({
  state,
  rejectReason,
}: {
  state: string;
  rejectReason?: string | null;
}) {
  const spec = STATE_META[state] ?? { tone: 'neutral' as const, label: state };
  return (
    <Badge tone={spec.tone} dot>
      {spec.label}
      {rejectReason && state === 'rejected' && (
        <span className="ml-1 font-normal opacity-80 truncate max-w-[12rem]" title={rejectReason}>
          · {rejectReason.slice(0, 40)}
        </span>
      )}
    </Badge>
  );
}






